import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import Head from 'expo-router/head';
import {
  useHeaderHeight,
  usePreventRemove,
} from 'expo-router/react-navigation';
import { useEffect, useState } from 'react';
import { YStack } from 'tamagui';

import { PSHeaderBackButtonOnTransparentHeader } from '@/components/PSHeaderButtons';
import MembershipPurchaseStatus from '@/components/pages/MembershipPurchaseStatus';
import authApi from '@/src/api/authApi';
import { useMembershipPlans } from '@/src/hooks/useMembershipPlans';
import { usePollMembership } from '@/src/hooks/usePollMembership';
import { useInsetStyle } from '@/src/lib/safeAreaInset';
import { setRestoredUser, useSessionUser } from '@/src/lib/sessionStore';

/**
 * The waiting room of a pass purchase — where the confirm step lands the user
 * once Razorpay's sheet closes with the payment submitted.
 *
 * The sheet closing proves nothing: the membership goes live when Razorpay's
 * webhook reaches OUR server, so this screen exists to wait for it
 * ([[usePollMembership]]). It's reached with `router.replace` — the confirm step
 * is gone from the history, so there's no back-and-buy-again.
 *
 * Two ids, because they answer different questions: `id` is the MEMBERSHIP,
 * which is what's polled, and `plan` is the plan it was bought from, read only
 * for the eyebrow and the panel's look while the membership is still in flight. Once the membership
 * arrives it names itself.
 *
 * ── Leaving is one-way ──
 * Every exit lands on HOME, and takes the memberships routes off the stack with
 * it (see `isLeaving` below). This screen is `replace`d in over the purchase, so
 * what sits underneath is the sales page the user came in through — and dropping
 * someone who just paid back onto "All plans" reads as an invitation to buy
 * again.
 *
 * What it draws is [[MembershipPurchaseStatus]]; this route only wires it up.
 */
export default function MembershipPurchaseStatusPage() {
  const { id, plan } = useLocalSearchParams<{ id: string; plan?: string }>();
  const { plans } = useMembershipPlans();
  const user = useSessionUser();
  const poll = usePollMembership(id);
  const router = useRouter();
  const headerHeight = useHeaderHeight();

  useInsetStyle('dark');

  // ── Leaving ─────────────────────────────────────────────────────────────
  // ONE exit, `setIsLeaving(true)`, shared by the CTA, the header ←, the swipe
  // gesture and Android's hardware back — the last two aren't ours to wire, so
  // `usePreventRemove` catches them: it blocks the removal (and on native sets
  // `preventNativeDismiss`, so the cancelled swipe comes back as a pop this same
  // listener sees) and hands us the moment instead.
  //
  // It blocks EVERY removal while armed, `dismissTo`'s pop included — hence the
  // flag rather than a call in the callback: disarming and dismissing have to
  // happen in that order, which is what the effect is for.
  //
  // `dismissTo` and not `replace`: popping TO home is what leaves nothing of the
  // purchase behind it, where a replace would swap this screen for a second home
  // with `/memberships` still under it. Deep-linked straight here, there's no
  // home in the stack to pop to and it replaces instead.
  const [isLeaving, setIsLeaving] = useState(false);
  usePreventRemove(!isLeaving, () => setIsLeaving(true));
  useEffect(() => {
    if (isLeaving) router.dismissTo('/');
  }, [isLeaving, router]);

  const shareSocialLink = async (url: string) => {
    // Sync the session: the saved link is what prefills the same ask on an
    // approval-only event, so a stale user would ask for it again.
    const { user: updated } = await authApi.updateMe({ socialMediaLink: url });
    setRestoredUser(updated);
  };

  return (
    <YStack flex={1} bg={'$color.background'}>
      <Head>
        <title>Purchase status · Putting Scene</title>
      </Head>
      <Stack.Screen
        options={{
          headerTransparent: true,
          headerTitle: () => <></>,
          ...panelHeaderOptions(() => setIsLeaving(true)),
        }}
      />

      <MembershipPurchaseStatus
        poll={poll}
        plan={plans?.find((candidate) => candidate.id === plan)}
        canAskForSocialLink={!!user && !user.socialMediaLink}
        onShareSocialLink={shareSocialLink}
        onLeave={() => setIsLeaving(true)}
        headerHeight={headerHeight}
      />
    </YStack>
  );
}

/**
 * The ←, in the native header's slot. Wired the two ways
 * [[authHeaderLeftOptions]] documents: the plain custom view (Android, iOS < 26)
 * plus a `custom` item that opts out of iOS 26's shared glass capsule. No
 * headerRight — the wordmark now carries the branding in the panel itself.
 *
 * The back control is a DEVIATION from this screen's original no-back rule — the
 * design puts one in the panel. It does NOT pop: `onBack` is the screen's one
 * exit, which leaves for home (see `isLeaving`).
 */
function panelHeaderOptions(onBack: () => void) {
  return {
    headerLeft: () => <PSHeaderBackButtonOnTransparentHeader />,
    unstable_headerLeftItems: () => [
      {
        type: 'custom' as const,
        element: <PSHeaderBackButtonOnTransparentHeader />,
        hidesSharedBackground: true,
      },
    ],
  };
}
